import React, { ChangeEvent, KeyboardEvent, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material//Delete';
import { styled } from '@mui/material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import { MAX_DROPDOWN_OPTIONS } from 'common/constants/firms';
import { Option } from 'common/types/firm';
import theme from 'theme';
import FormInput from './FormInput';
import { DropdownOptionsProps } from './types';

const PREFIX = 'DropdownOptions';

const classes = {
  deleteButton: `${PREFIX}-deleteButton`,
  box: `${PREFIX}-box`,
  optionName: `${PREFIX}-optionName`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
  [`& .${classes.deleteButton}`]: {
    color: theme.palette.grey[400],
    padding: '0',
  },
  [`& .${classes.box}`]: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    padding: '0.5rem',
    margin: '0.3rem 0',
  },
  [`& .${classes.optionName}`]: {
    fontWeight: 400,
    fontSize: '0.875rem',
    color: theme.palette.black,
    marginRight: 'auto',
  },
});

const DropdownOptions = ({
  formValues,
  setFormValues,
  isMaxOptions,
  questionOptions: optionsFromDB,
}: DropdownOptionsProps) => {
  const { options } = formValues;
  const [inputValue, setInputValue] = useState('');
  const [isMaxCharactersError, setIsMaxCharactersError] = useState(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleAddValueToOptions = (optionParam?: Option) => {
    let optionName = '';
    let optionId = '';

    if (optionParam) {
      optionName = optionParam.name;
      optionId = optionParam.id ? optionParam.id.toString() : '';
    } else if (inputValue.trim() !== '') {
      optionName = inputValue;
    }

    const optionExists = formValues.options.some(option => option.name === optionName);

    if (!optionExists) {
      setFormValues({
        ...formValues,
        options: [...formValues.options, { name: optionName, id: Number(optionId), is_deleted: false, deleted_at: '' }],
      });
      setInputValue('');
    }
  };

  const getOptionData = (): Option | undefined => optionsFromDB.find(opt => opt.name === inputValue);

  const optionAlreadyExists = (optionName: string): boolean =>
    formValues.options.some(option => option.name === optionName);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isMaxCharactersError) {
      // we capitalize the option value since all options in the database start with an uppercase letter
      // this way we avoid add "yes" and "Yes" as two different options
      const isExistingOption = optionsFromDB.some(option => option.name === capitalize(inputValue));
      if (!isExistingOption) {
        handleAddValueToOptions();
      }

      const option = getOptionData();
      if (option && !optionAlreadyExists(option.name)) {
        handleAddValueToOptions(option);
      }
    }
  };

  const handleDelete = (indexToDelete: number) => {
    setFormValues({
      ...formValues,
      options: formValues.options.filter((_, index) => index !== indexToDelete),
    });
  };

  return (
    <Root>
      {!isMaxOptions && (
        <FormInput
          name="optionName"
          label={
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Write an option and <strong>press Enter</strong> to add it ({MAX_DROPDOWN_OPTIONS} max options allowed)
            </Typography>
          }
          placeholder={isMaxOptions ? 'Max options reached' : 'Write a new option'}
          value={inputValue}
          handleInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          maxCharacters={80}
          setIsMaxCharactersError={setIsMaxCharactersError}
          disabled={isMaxOptions}
          marginButton={2}
          focusOnMount
        />
      )}
      <Box />
      {options.map((option, index) => (
        <Box key={option.id} className={classes.box} display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.optionName}>{option.name}</Typography>

          {/* Delete Button */}
          <Tooltip title="Remove option">
            <IconButton
              className={classes.deleteButton}
              aria-label="delete"
              size="small"
              onClick={() => handleDelete(index)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Root>
  );
};

export default DropdownOptions;
